import {
    ComponentPropsWithoutRef,
    ForwardedRef,
    Suspense,
    createContext,
    forwardRef,
    useContext,
} from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { useToast } from '@givebutter/primitives/toast';
import {
    faArrowRightArrowLeft,
    faCode,
    faCog,
    faEllipsisVertical,
    faPlus,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateWidget } from 'api/widgets';
import {
    transformWidgetFormValuesIntoRequest,
    transformWidgetIntoFormValues,
} from 'assets/js/pages/account-settings/account-integrations/widgets-form';
import * as Dialog from 'components/dialog';
import * as DropdownMenu from 'components/dropdown-menu';
import { GivebutterFormProvider, UseFormSubmitButton, useGivebutterForm } from 'components/form';
import * as WidgetForm from 'components/form/WidgetForm';
import * as Tooltip from 'components/tooltip';
import * as DeleteWidgetModal from 'components/widgets/DeleteWidgetModal';
import { useDeleteWidgetMutation } from 'components/widgets/DeleteWidgetModal';
import { WidgetLivePreview } from 'components/widgets/WidgetLivePreview';
import { useCampaignWidgets } from 'customHooks/useCampaignWidgets';
import { useCreateWidgetMutation } from 'customHooks/useCreateWidgetMutation';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { queryClient } from 'queries/queryClient';
import { widgetQueryKeys } from 'queries/widget-query-keys';
import { Widget } from 'types/domain';

interface IWidgetCardContext {
    widget: Partial<Widget>;
}

const WidgetCardContext = createContext<IWidgetCardContext>({ widget: {} });
const WidgetCard = WidgetCardContext.Provider;

const WidgetCardContent = forwardRef(
    (props: ComponentPropsWithoutRef<'div'>, ref: ForwardedRef<HTMLDivElement>) => {
        const { children, className = '', ...rest } = props;

        return (
            <div
                ref={ref}
                className={`relative flex flex-col justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm ${className}`}
                {...rest}
            >
                <Suspense fallback={null}>
                    {children || (
                        <>
                            <div className={`flex flex-col gap-4`}>
                                <WidgetCardPreview />
                                <WidgetCardDescription />
                            </div>
                            <WidgetCardActionBar />
                        </>
                    )}
                </Suspense>
            </div>
        );
    },
);

const WidgetCardPreview = forwardRef(
    (props: ComponentPropsWithoutRef<'div'>, ref: ForwardedRef<HTMLDivElement>) => {
        const { className = '', ...rest } = props;
        const { widget } = useContext(WidgetCardContext);

        const isThermometer = widget.config?.type === 'thermometer';

        return (
            <div
                ref={ref}
                className={`flex h-40 ${
                    isThermometer || widget.type === 'giving-form' || widget.type === 'signup-form'
                        ? 'items-start'
                        : 'items-center'
                } justify-center overflow-x-auto overflow-y-hidden rounded-xl bg-gray-50 ${className}`}
                {...rest}
            >
                <WidgetLivePreview
                    type={widget.type}
                    campaign={widget.campaign}
                    label={widget.config?.label}
                    backgroundColor={widget.config?.backgroundColor}
                    borderWidth={widget.config?.borderWidth}
                    borderColor={widget.config?.borderColor}
                    labelColor={widget.config?.labelColor}
                    borderRadius={widget.config?.borderRadius}
                    icon={widget.config?.icon}
                    iconPosition={widget.config?.iconPosition}
                    dropShadow={widget.config?.dropShadow}
                    textColor={widget.config?.textColor}
                    progressBarColor={widget.config?.progressBarColor}
                    size={widget.config?.size}
                    goalBarType={widget.config?.type ?? undefined}
                    showGoalAmount={isThermometer ? false : widget.config?.showGoalAmount}
                    showRaisedAmount={isThermometer ? false : widget.config?.showRaisedAmount}
                    showRaisedPercentage={
                        isThermometer ? false : widget.config?.showRaisedPercentage
                    }
                    layout={widget.config?.layout}
                    image={widget.config?.image}
                    imageSizing={widget.config?.imageSizing}
                    title={widget.config?.title}
                    description={widget.config?.description}
                />
            </div>
        );
    },
);

const WidgetCardDescription = forwardRef(
    (props: ComponentPropsWithoutRef<'div'>, ref: ForwardedRef<HTMLDivElement>) => {
        const { className = '', ...rest } = props;
        const match = useRouteMatch<{ campaign_id?: string }>();
        const { widget } = useContext(WidgetCardContext);

        const typeToTitleMapping = {
            button: 'Button',
            'giving-form': 'Form',
            'signup-form': 'Signup form',
            'goal-bar': 'Goal bar',
        };

        const typeToDescriptionMapping = {
            button: "Embed a button anywhere on your website that pops open your campaign's donation form.",
            'giving-form': 'Embed your donation form directly onto your website.',
            'signup-form': 'Embed your signup form directly onto your website.',
            'goal-bar':
                "Showcase the progress of your campaign's success with a goal bar reflecting the performance in real time.",
        };

        const title = widget.name || typeToTitleMapping[widget.type ?? 'button'];
        const description =
            widget.campaign?.title && !match.params.campaign_id
                ? widget.campaign?.title
                : typeToDescriptionMapping[widget.type ?? 'button'];

        return (
            <div ref={ref} className={`flex flex-col gap-1 ${className}`} {...rest}>
                <div className="font-medium">{title}</div>
                <div className="min-h-[4rem] text-sm text-gray-600">{description}</div>
            </div>
        );
    },
);

const WidgetCardActionBar = forwardRef(
    (props: ComponentPropsWithoutRef<'div'>, ref: ForwardedRef<HTMLDivElement>) => {
        const { className = '', ...rest } = props;

        return (
            <div ref={ref} className={`flex gap-2 ${className}`} {...rest}>
                <WidgetCardEmbedTrigger />
                <WidgetCardEditTrigger />
                <WidgetCardDropdownTrigger />
            </div>
        );
    },
);

const WidgetCardEmbedTrigger = () => {
    const { accountId } = useCurrentAccount();
    const { widget } = useContext(WidgetCardContext);
    const match = useRouteMatch<{ campaign_id?: string }>();
    const campaignId = Number(widget.campaign_id);
    const { data: campaign } = useCurrentCampaign({ campaignId });
    const history = useHistory();

    const { isPending: isLoading, mutate: createWidgetMutation } = useCreateWidgetMutation({
        onSuccess: newWidget => {
            queryClient.invalidateQueries({ queryKey: widgetQueryKeys.widgets(accountId) });
            openWidgetComponentSetupModal(newWidget.id);
        },
    });

    const openWidgetComponentSetupModal = (widgetId: number) => {
        if (match.params.campaign_id) {
            history.push(
                `/accounts/${accountId}/campaigns/${match.params.campaign_id}/sharing/widgets/${widgetId}/embed`,
            );
        } else {
            history.push(`/accounts/${accountId}/settings/integrations/widgets/${widgetId}/embed`);
        }
    };

    const isGoalBarWithoutGoal = widget!.type === 'goal-bar' && campaign && !campaign.goal;

    return (
        <div className="flex-1">
            <LoadingOverlay isLoading={isLoading}>
                <Tooltip.Root>
                    <Tooltip.Trigger asChild className="w-full">
                        <div>
                            <Button
                                disabled={isGoalBarWithoutGoal}
                                variant="outline"
                                color="neutral"
                                className={`w-full !rounded-lg`}
                                onClick={() => {
                                    if (widget.id) {
                                        return openWidgetComponentSetupModal(widget.id);
                                    }
                                    return createWidgetMutation({
                                        widgetType: widget.type ?? 'button',
                                        campaignId: parseInt(match.params.campaign_id ?? ''),
                                    });
                                }}
                            >
                                <FontAwesomeIcon icon={faCode} />
                                Embed
                            </Button>
                        </div>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content inverse>
                            <div>
                                {isGoalBarWithoutGoal
                                    ? "The campaign doesn't have a goal yet"
                                    : 'Embed instructions'}
                            </div>
                            <Tooltip.Arrow inverse />
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>
            </LoadingOverlay>
        </div>
    );
};

const WidgetCardEditTrigger = () => {
    const { accountId } = useCurrentAccount();
    const { widget } = useContext(WidgetCardContext);
    const match = useRouteMatch<{ campaign_id?: string }>();
    const campaignId = Number(widget.campaign_id);
    const { data: campaign } = useCurrentCampaign({ campaignId });
    const history = useHistory();

    const { isPending: isLoading, mutate: createWidgetMutation } = useCreateWidgetMutation({
        onSuccess: newWidget => {
            queryClient.invalidateQueries({ queryKey: widgetQueryKeys.widgets(accountId) });
            showEditWidgetPage(newWidget.id);
        },
    });

    const showEditWidgetPage = (widgetId: number) => {
        if (match.params.campaign_id) {
            history.push(
                `/accounts/${accountId}/campaigns/${match.params.campaign_id}/sharing/widgets/${widgetId}/config`,
            );
        } else {
            history.push(`/accounts/${accountId}/settings/integrations/widgets/${widgetId}/config`);
        }
    };

    const isGoalBarWithoutGoal = widget!.type === 'goal-bar' && campaign && !campaign.goal;

    return (
        <LoadingOverlay isLoading={isLoading}>
            <Tooltip.Root>
                <Tooltip.Trigger asChild>
                    <div>
                        <Button
                            disabled={isGoalBarWithoutGoal}
                            variant="outline"
                            color="neutral"
                            shape={'square'}
                            className={`!rounded-lg`}
                            onClick={() => {
                                if (widget.id) {
                                    return showEditWidgetPage(widget.id);
                                }
                                return createWidgetMutation({
                                    widgetType: widget.type ?? 'button',
                                    campaignId: parseInt(match.params.campaign_id ?? ''),
                                });
                            }}
                            aria-label="Create"
                        >
                            <FontAwesomeIcon icon={faCog} />
                        </Button>
                    </div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content inverse>
                        <div>
                            {isGoalBarWithoutGoal
                                ? "The campaign doesn't have a goal yet"
                                : 'Customize'}
                        </div>
                        <Tooltip.Arrow inverse />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </LoadingOverlay>
    );
};

const WidgetCardDropdownTrigger = () => {
    const toast = useToast();
    const { widget } = useContext(WidgetCardContext);
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const campaignId = widget.campaign_id ?? 0;

    const { isPending: isLoading, mutate: createWidgetMutation } = useCreateWidgetMutation({
        onSuccess: newWidget => {
            queryClient.invalidateQueries({ queryKey: widgetQueryKeys.widgets(accountId) });
            history.push(
                `/accounts/${accountId}/campaigns/${campaignId}/sharing/widgets/${newWidget.id}/config`,
            );
        },
    });

    const methods = useGivebutterForm<WidgetForm.WidgetFormValues>({
        defaultValues: transformWidgetIntoFormValues(widget as Widget),
    });
    const { handleSubmit, resetFormButton } = methods;
    const { mutate: submit } = useMutation({
        mutationFn: (values: WidgetForm.WidgetFormValues) =>
            updateWidget({
                accountId,
                widgetId: widget.id ?? 0,
                widgetConfig: transformWidgetFormValuesIntoRequest(values),
            }),
        onSuccess: newWidget => {
            queryClient.invalidateQueries({ queryKey: widgetQueryKeys.widgets(accountId) });
            toast.success('Widget updated successfully!');
            history.push(
                `/accounts/${accountId}/campaigns/${newWidget.campaign_id}/sharing/widgets`,
            );
        },
        onError: () => {
            toast.error('Unable to update widget');
            resetFormButton();
        },
    });

    return (
        !!widget.id && (
            <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                    <Button variant="outline" color="neutral" shape={'square'} aria-label="More">
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                    </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                    <Dialog.Root>
                        <Dialog.Trigger asChild>
                            <DropdownMenu.Item
                                className="flex gap-2"
                                onSelect={e => e.preventDefault()}
                            >
                                <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                                Transfer widget
                            </DropdownMenu.Item>
                        </Dialog.Trigger>
                        <Dialog.Portal>
                            <Dialog.Overlay />
                            <Dialog.Content>
                                <GivebutterFormProvider {...methods}>
                                    <form
                                        onSubmit={handleSubmit(
                                            submit as SubmitHandler<WidgetForm.WidgetFormValues>,
                                        )}
                                    >
                                        <Dialog.Close />
                                        <Dialog.Body className="flex flex-col gap-4">
                                            <Dialog.Close />
                                            <FontAwesomeIcon
                                                icon={faArrowRightArrowLeft}
                                                className="flex h-5 w-5 rounded-full bg-gray-200 p-3"
                                            />
                                            <div>
                                                <div className="text-left text-xl font-medium">
                                                    Transfer widget to another campaign?
                                                </div>
                                                <div className="text-gray-700">
                                                    Redirect your widget's traffic to a different
                                                    campaign without the need to re-embed.
                                                </div>
                                            </div>
                                            <WidgetForm.CampaignInput />
                                            <div className="flex justify-between gap-4">
                                                <Dialog.Cancel asChild>
                                                    <Button
                                                        variant="outline"
                                                        color="neutral"
                                                        className="w-auto"
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Dialog.Cancel>
                                                <UseFormSubmitButton>Transfer</UseFormSubmitButton>
                                            </div>
                                        </Dialog.Body>
                                    </form>
                                </GivebutterFormProvider>
                            </Dialog.Content>
                        </Dialog.Portal>
                    </Dialog.Root>
                    {widget.type === 'button' && (
                        <LoadingOverlay isLoading={isLoading}>
                            <DropdownMenu.Item
                                className="flex gap-2"
                                onClick={() =>
                                    createWidgetMutation({
                                        widgetType: widget.type ?? 'button',
                                        campaignId: campaignId,
                                    })
                                }
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Create new button
                            </DropdownMenu.Item>
                        </LoadingOverlay>
                    )}
                    <Suspense fallback={null}>
                        <DeleteWidgetDropdown />
                    </Suspense>
                </DropdownMenu.Content>
            </DropdownMenu.Root>
        )
    );
};

const DeleteWidgetDropdown = () => {
    const { widget } = useContext(WidgetCardContext);
    const campaignId = widget.campaign_id ?? 0;
    const { data: widgets } = useCampaignWidgets(campaignId);
    const { mutate: deleteWidget } = useDeleteWidgetMutation({
        widgetId: widget.id ?? 0,
    });

    const hasOnlyOneOfType = (widgets.filter(w => w.type === widget.type) ?? []).length === 1;

    return (
        !hasOnlyOneOfType && (
            <DeleteWidgetModal.Root onDelete={deleteWidget}>
                <DeleteWidgetModal.Trigger asChild>
                    <DropdownMenu.Item
                        className="flex gap-2 text-red-500 hover:!text-red-500"
                        onSelect={e => e.preventDefault()}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                        Delete widget
                    </DropdownMenu.Item>
                </DeleteWidgetModal.Trigger>
            </DeleteWidgetModal.Root>
        )
    );
};

const Root = WidgetCard;
const Content = WidgetCardContent;
const Preview = WidgetCardPreview;
const Description = WidgetCardDescription;
const ActionBar = WidgetCardActionBar;
const DropdownTrigger = WidgetCardDropdownTrigger;
const EmbedTrigger = WidgetCardEmbedTrigger;
const EditTrigger = WidgetCardEditTrigger;

export {
    Root,
    Content,
    Preview,
    Description,
    ActionBar,
    DropdownTrigger,
    EmbedTrigger,
    EditTrigger,
    //
    WidgetCard,
    WidgetCardContent,
    WidgetCardPreview,
    WidgetCardDescription,
    WidgetCardActionBar,
    WidgetCardDropdownTrigger,
    WidgetCardEmbedTrigger,
    WidgetCardEditTrigger,
};
